import React, { useState, useEffect, useMemo } from 'react';
import { supabase, Site } from '../lib/supabase';
import {
  BedDouble, MapPin, Calendar, Moon, Search, Building2, ArrowRight, AlertCircle, Users,
} from 'lucide-react';

const NIGHT_OPTIONS = [1, 2, 3, 4, 5];
const PEOPLE_OPTIONS = [1, 2, 3, 4, 5];

const formatDateForInput = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDisplayDate = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const addDays = (dateStr: string, days: number): string => {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return formatDateForInput(d);
};

const toAirbnbDate = (dateStr: string): string => {
  // Airbnb expects YYYY-MM-DD
  return dateStr;
};

export const AccommodationPage: React.FC = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [checkIn, setCheckIn] = useState<string>(formatDateForInput(new Date()));
  const [nights, setNights] = useState<number>(4);
  const [people, setPeople] = useState<number>(1);

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const { data, error } = await supabase
        .from('sites')
        .select('*')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setSites(data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedSite = useMemo(
    () => sites.find((s) => s.id === selectedSiteId) || null,
    [sites, selectedSiteId]
  );

  const checkOut = useMemo(() => addDays(checkIn, nights), [checkIn, nights]);

  const airbnbUrl = useMemo(() => {
    if (!selectedSite) return '';

    const checkInStr = toAirbnbDate(checkIn);
    const checkOutStr = toAirbnbDate(checkOut);

    // Build a location query string — prefer the address since Airbnb's
    // search understands plain-text addresses well. Fall back to lat/long
    // only if no address is available.
    let locationQuery: string;
    if (selectedSite.address) {
      locationQuery = selectedSite.address;
    } else if (selectedSite.latitude && selectedSite.longitude) {
      locationQuery = `${selectedSite.latitude},${selectedSite.longitude}`;
    } else {
      locationQuery = selectedSite.name;
    }

    // Current Airbnb search URL format (2024+):
    // https://www.airbnb.com.au/s/homes?query={location}&checkin={YYYY-MM-DD}&checkout={YYYY-MM-DD}&adults={n}
    const params = new URLSearchParams({
      query: locationQuery,
      checkin: checkInStr,
      checkout: checkOutStr,
      adults: String(people),
    });

    return `https://www.airbnb.com.au/s/homes?${params.toString()}`;
  }, [selectedSite, checkIn, checkOut, people]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Accommodation</h1>
        <p className="text-sm text-gray-500 mt-1">
          Search Airbnb for short-stay accommodation near your active job sites.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Selection Panel */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <BedDouble className="h-5 w-5 text-orange-600" />
            Search Details
          </h3>

          <div className="space-y-5">
            {/* Site Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select a Site
              </label>
              <select
                value={selectedSiteId}
                onChange={(e) => setSelectedSiteId(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="">Choose a site...</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name} — {site.address}
                  </option>
                ))}
              </select>
            </div>

            {/* Check-in Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="h-4 w-4 inline mr-1 text-gray-500" />
                Check-in Date
              </label>
              <input
                type="date"
                value={checkIn}
                min={formatDateForInput(new Date())}
                onChange={(e) => setCheckIn(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
              />
            </div>

            {/* Number of Nights */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Moon className="h-4 w-4 inline mr-1 text-gray-500" />
                Number of Nights
              </label>
              <div className="flex gap-2">
                {NIGHT_OPTIONS.map((n) => (
                  <button
                    key={n}
                    onClick={() => setNights(n)}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      nights === n
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {n} {n === 1 ? 'night' : 'nights'}
                  </button>
                ))}
              </div>
            </div>

            {/* Number of People */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="h-4 w-4 inline mr-1 text-gray-500" />
                Number of People
              </label>
              <div className="flex gap-2">
                {PEOPLE_OPTIONS.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeople(p)}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      people === p
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {p} {p === 1 ? 'person' : 'people'}
                  </button>
                ))}
              </div>
            </div>

            {/* Check-out Date (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Check-out Date
              </label>
              <div className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                {formatDisplayDate(checkOut)}
              </div>
            </div>
          </div>
        </div>

        {/* Summary & Search Panel */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Search className="h-5 w-5 text-orange-600" />
            Search Summary
          </h3>

          {!selectedSite ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="h-14 w-14 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Building2 className="h-7 w-7 text-gray-400" />
              </div>
              <h4 className="text-base font-medium text-gray-900 mb-1">No site selected</h4>
              <p className="text-sm text-gray-500 max-w-xs">
                Select an active job site to search for nearby accommodation on Airbnb.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Site Info */}
              <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border border-orange-100">
                <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-5 w-5 text-orange-600" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{selectedSite.name}</p>
                  <div className="flex items-start gap-1 text-sm text-gray-500 mt-0.5">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    <span className="truncate">{selectedSite.address}</span>
                  </div>
                </div>
              </div>

              {/* Date Summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-500 mb-1">Check-in</p>
                  <p className="text-sm font-semibold text-gray-900">{formatDisplayDate(checkIn)}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-500 mb-1">Check-out</p>
                  <p className="text-sm font-semibold text-gray-900">{formatDisplayDate(checkOut)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <span className="text-sm font-medium text-blue-900">Total Stay</span>
                  <span className="text-sm font-semibold text-blue-700">
                    {nights} {nights === 1 ? 'night' : 'nights'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <span className="text-sm font-medium text-blue-900">Guests</span>
                  <span className="text-sm font-semibold text-blue-700">
                    {people} {people === 1 ? 'person' : 'people'}
                  </span>
                </div>
              </div>

              {/* Search Button */}
              <a
                href={airbnbUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3.5 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors group"
              >
                <Search className="h-5 w-5" />
                <span>Search Airbnb</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>

              {/* Info Note */}
              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg">
                <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  Clicking "Search Airbnb" opens Airbnb in a new tab with the location and dates
                  pre-filled. You will complete your booking directly on Airbnb.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {sites.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No active sites</h3>
          <p className="text-gray-500">Add active sites to search for nearby accommodation.</p>
        </div>
      )}
    </div>
  );
};